/**
 * Which player a monster hunts, and what it may do about a player above it (02 §5 Zen-chan's
 * "alternates which of the two it may do"). Ported from D_1CBD's OPPTR choice and D_EE62.
 */

import type { Monster, PlayerId, World } from '../types';

/**
 * D_1CBD $1CE3: a monster in an even slot hunts player 1, odd player 2 - when that player is in
 * normal play (state 1: not dying, not respawning, which is state $10 until the invulnerability
 * runs out, spawn_timer $290D); otherwise the other one, whatever its state.
 */
export function huntedPlayer(world: World, m: Monster): PlayerId {
  const own: PlayerId = (m.ai.slot & 1) === 1 ? 1 : 0;
  const p = world.players[own];
  return p.state === 'play' && p.invulnerable === 0 ? own : own === 0 ? 1 : 0;
}

/**
 * The hunted player's position as the monster reads it. A player out of the game has X and Y 0 on
 * the C64 ($04F0 clears them on game over) [U: never-joined player 2 assumed the same].
 */
const gone = (world: World, id: PlayerId): boolean =>
  world.players[id].state === 'out' || world.players[id].state === 'gameOver';
export const playerX = (world: World, id: PlayerId): number => (gone(world, id) ? 0 : world.players[id].x);
export const playerY = (world: World, id: PlayerId): number => (gone(world, id) ? 0 : world.players[id].y);

/**
 * D_EE62, run at the start of every walker AI update. Player level with the monster: gap jumps
 * allowed. Player below: no jumps, and any pending jump request (joy bit 2) is dropped. Player
 * above: every `interval` updates (30 + rand 0..31 for Zen-chan, rolled at spawn) the permission
 * flips between "gap jumps" and "gap and vertical jumps" (L_EE91; the gap test at L_EA92 reads
 * either flag, so the vertical phase allows both).
 */
export function watchPlayer(world: World, m: Monster, target: PlayerId): void {
  const a = m.ai;
  const py = playerY(world, target);
  if (py === m.y) {
    a.gapOk = true;
    a.vertOk = false;
    a.joy &= 0x0b;
  } else if (py > m.y) {
    a.gapOk = false;
    a.vertOk = false;
    a.joy &= 0x0b;
  } else {
    a.timer = (a.timer - 1) & 0xff;
    if (a.timer !== 0) return;
    // Set the flag the index points at, clear the other, then point at the other (`eor #$28`).
    a.gapOk = !a.nextVert;
    a.vertOk = a.nextVert;
    a.nextVert = !a.nextVert;
    a.timer = a.interval;
  }
}
